import {
  XCEL40_FRONT_X,
  XCEL40_HALF_WIDTH_M,
  XCEL40_ROOF_Z,
  XCEL40_SIZE_SCALE,
} from './xcel40Model'
import {
  bearingBetween,
  metresBetween,
  seedTrail as seedConsistTrail,
  type TrailPoint,
} from './trainConsist'

// Time constant (s) for the shortest-angle exponential ease that smooths each rendered
// model's heading toward its live target. ~0.18 s converges in roughly half a second,
// short enough to track real turns, long enough to absorb a tangent step at a shape
// vertex / segment hand-off so the 3D model never snap-rotates or spins.
export const HEADING_TAU = 0.18

// Hard ceiling (m/s) on how fast a rendered train may CLOSE a gap to its live schedule
// target. The within-segment cap is 1.5× the segment's nominal speed, which is smooth on
// normal segments, but segment timing is frozen at adoption while the feed keeps revising,
// so a train running behind the feed adopts a hand-off segment whose duration has collapsed
// (feed's next arrival is imminent while the model is still stops back). Its nominal speed is
// then huge, so 1.5× nominal still darts the model across the gap in a frame or two. Bounding
// the close rate to a physical speed turns that dart into a brief, smooth, faster-than-normal
// catch-up. ~30 m/s (~67 mph) sits above the real NYC subway top speed, so it never throttles
// legitimate motion (steady-state tracking is always ≤ real speed ≤ this), only teleports.
export const MAX_CATCHUP_MPS = 30

// Critically-damped follow time (s) for easing each train's rendered `progress` toward its
// live wall-clock target. This replaces the old hard per-frame step clamp, whose two regimes
// (constant-rate catch-up vs. exact clock-tracking) met at a VELOCITY discontinuity: any
// frame with a large dt (a 15 s poll's heavy processing frame, a GC pause) let the unclamped
// target leap ahead while the clamped step fell behind, so the model briefly stalled then
// sped up to re-sync. That speed-up/slow-down pulse, repeating each poll, was the residual
// forward stutter. A SmoothDamp converges to the target's OWN rate in steady state (constant
// on-screen velocity = real schedule speed) and absorbs dt spikes as a smooth, bounded change
// in speed rather than a step. ~0.7 s tracks real motion tightly (sub-second visual lag) while
// still smoothing out the worst poll/GC frame deltas.
export const PROGRESS_SMOOTH_TIME = 0.7

// Final-stage rendered-position follow. Even with a smooth `progress`, the rendered lon/lat
// can pop a few meters in one frame at a segment hand-off: the old segment's endpoint and the
// next segment's start point are each independently snapped to stop/track geometry and don't
// coincide exactly, so the model lurches at every stop. Confirmed by on-frame instrumentation:
// fps 175 / 0 drops / ~0ms render, but per-vehicle one-frame moves spiking from a steady 0.1 m
// to 4–6 m (and rarely ~300 m on a re-seed). Rather than chase every upstream discontinuity,
// the rendered position FOLLOWS its target with a critically-damped 2-D SmoothDamp: normal
// motion (~0.1 m/frame) passes through with an imperceptible constant lag, while any pop becomes
// a sub-second, velocity-continuous glide: the model simply speeds up briefly to catch its
// target, exactly as intended, and can never teleport. ~0.18 s keeps it tight to truth.
export const RENDER_POS_TAU = 0.18
// A target that leaps farther than this in one frame is a genuine re-placement (off-screen
// re-entry, or a fresh re-seed after the vehicle aged out), not a hand-off pop. Gliding the
// model across the city would be worse than an unseen snap, so above this we snap instead.
export const RENDER_SNAP_M = 150

// A vehicle whose glide state hasn't been integrated for this long was not being rendered:
// the tab was hidden (rAF paused), the vehicle was filtered out of the list, or it was
// pruned from the store and later re-emitted by the feed. Its frozen segment / glide window
// is stale while the wall clock kept running, so RESUMING it draws the vehicle wherever it
// was when rendering stopped and then crawls it toward reality at the bounded catch-up
// speed: minutes of visibly wrong positions after a tab refocus. Instead, drop the stale
// state and re-seed from the live feed (the same first-sighting path), which places the
// vehicle at the schedule's CURRENT position; the per-car follow and trail re-seed turn the
// leap into a clean single-frame snap. Normal frame gaps are tens of ms (a heavy poll/GC
// frame a few hundred), so 5 s is unambiguously "this vehicle wasn't being rendered".
export const STALE_STATE_GAP_S = 5

export const ROUTE_OFFSETS: Record<string, number> = {
  '1': -12, '2': 0, '3': 12,
  '4': -12, '5': 0, '6': 12, '6X': 12,
  '7': 0,
  // BMT-Broadway group is shifted off the 6th-Ave IND band below so the two groups never
  // land on the same offset where they share a corridor. With both at {-18,-6,6,18}, F(6)
  // and R(6) drew on top of each other along Queens Blvd (orange appearing to "turn
  // yellow"), and B/N + D/Q stacked on the Manhattan Bridge. {-24,-12,0,12} keeps N,Q,R,W
  // distinct and ordered while interleaving cleanly with B,D,F,M on every shared segment.
  'N': -24, 'Q': -12, 'R': 0, 'W': 12,
  'A': -12, 'C': 0, 'E': 12,
  'B': -18, 'D': -6, 'F': 6, 'M': 18,
  'G': 0,
  'J': -6, 'Z': 6,
  'L': 0,
  'GS': -12, 'FS': 0, 'H': 12,
  'SI': 0,
}

export const ROUTE_ALIAS: Record<string, string> = { FX: 'F', '6X': '6', '7X': '7' }

// The zoom ramp `rl-casing`/`rl-fill` apply to ROUTE_OFFSETS. MapLibre interpolates it in
// the style and the vehicle layer has to reproduce it in JS, so both read this ONE stop
// table: if the two ever disagree, every train renders a fixed distance off its own
// coloured line. Ascending in zoom, clamped at both ends (MapLibre's own `interpolate`
// semantics), which is what routeLineOffsetScale implements.
export const ROUTE_OFFSET_STOPS: ReadonlyArray<readonly [zoom: number, scale: number]> = [
  [8, 0.25],
  [12, 0.7],
  [15, 1],
]

export function routeLineOffsetScale(zoom: number): number {
  const first = ROUTE_OFFSET_STOPS[0]
  const last = ROUTE_OFFSET_STOPS[ROUTE_OFFSET_STOPS.length - 1]
  if (zoom <= first[0]) return first[1]
  if (zoom >= last[0]) return last[1]
  for (let i = 1; i < ROUTE_OFFSET_STOPS.length; i++) {
    const [z0, s0] = ROUTE_OFFSET_STOPS[i - 1]
    const [z1, s1] = ROUTE_OFFSET_STOPS[i]
    if (zoom < z1) return s0 + ((zoom - z0) / (z1 - z0)) * (s1 - s0)
  }
  return last[1]
}

/** The same ramp as a MapLibre `line-offset` expression over the feature's `offset`. */
export function routeLineOffsetExpression(): unknown[] {
  return [
    'interpolate', ['linear'], ['zoom'],
    ...ROUTE_OFFSET_STOPS.flatMap(([zoom, scale]) => [zoom, ['*', ['get', 'offset'], scale]]),
  ]
}

// Ground metres one screen pixel covers at `zoom`/`lat` (MapLibre's 512 px world tile).
// ROUTE_OFFSETS is in SCREEN pixels, but vehicles are placed in world coordinates, so the
// offset has to be converted at the live zoom every frame.
export function metresPerPixel(zoom: number, lat: number): number {
  return (40_075_016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom)
}

// Lateral shift, in metres, that puts a vehicle on the route line actually DRAWN for its
// route instead of on the raw GTFS shape. Route lines are fanned out sideways so the four
// routes sharing a trunk stay legible (see ROUTE_OFFSETS), which means the true shape a
// train rides is up to ~24 px away from its own colored line; at zoom 15 that is ~45 m,
// far enough that the train reads as running beside the line rather than on it. Positive
// `line-offset` is to the RIGHT of the shape's vertex order, hence the `dir` factor.
export function routeLineOffsetMetres(
  routeId: string,
  zoom: number,
  lat: number,
  dir: 1 | -1 = 1,
): number {
  const px = ROUTE_OFFSETS[ROUTE_ALIAS[routeId] ?? routeId] ?? 0
  if (!px) return 0
  return px * routeLineOffsetScale(zoom) * metresPerPixel(zoom, lat) * dir
}

// Max combined (cosLat-corrected) squared-degree distance from a stop pair to a
// shape for that shape to count as covering the segment: ≈350 m per stop.
// Comfortably above normal on-route vertex spacing, far below the kilometre-scale
// gap a wrong route produces (e.g. an F train rerouted onto the G's Crosstown line).
export const SEG_MATCH_TOL_SQ = 2e-5

// ---------------------------------------------------------------------------
// Subway consist geometry.
// X = direction of travel, Y = car width, Z = up. All values in metres.
//
// The CAR ITSELF is no longer built here: the R160 is an authored glTF loaded by a
// ScenegraphLayer, and everything about its shape, materials and decals lives in
// r160Model.ts. What remains is the formation - how many cars, how far apart, and the
// breadcrumb trail (see TrailPt) each follower samples at its coupling distance so the
// train bends along the route the front car actually traced. Cars never sample track
// geometry independently; that per-car sampling was the original source of the
// stutter/teleport glitches and must not come back.
// ---------------------------------------------------------------------------
export type V3 = [number, number, number]
// Cars per train, spread end-to-end along the trail above TRAIN_CARS_MIN_ZOOM and
// collapsed to the single lead datum below it.
export const TRAIN_CARS = 8
// The R160 draws no mesh at all until R160_MESH_MIN_ZOOM, which sits ABOVE this: by the
// time a mesh exists the consist is already full-length, so the single stand-in car is
// only ever a datum-count state, never something on screen. Don't raise this past the
// mesh floor - that is the ordering the R160 LOD ladder assumes (and asserts).
export const TRAIN_CARS_MIN_ZOOM = 13.5
export const TRAIN_CARS_EXIT_ZOOM = 13.2
// Distance between adjacent car centres in map-metres.
// The authored R160 is 18.352 m over anticlimbers; retain a small coupler gap.
export const CAR_SPACING_M = 18.352 + 0.30
// Longitudinal distance between the two truck rotation centres on the authored R160.
// A car body is aligned from these two rail-contact samples instead of a tangent at its
// centre; otherwise it appears to pivot around one imaginary truck in the middle.
export const CAR_TRUCK_CENTRES_M = 13.589
// Breadcrumb-trail recording: a new point once the centre has moved this far. Small
// enough that the polyline hugs every curve at rendered scale, large enough that a
// dwelling train records nothing.
export const TRAIL_STEP_M = 1.5
// Arc length of trail retained per train: enough to place every follower car plus
// slack so pruning never bites the rear car's sample point.
export const TRAIL_KEEP_M = (TRAIN_CARS - 1) * CAR_SPACING_M + 80

export function buildMesh(quads: Array<{ pts: [V3, V3, V3, V3]; n: V3 }>) {
  const pos: number[] = [], nrm: number[] = []
  for (const { pts: [a, b, c, d], n } of quads) {
    for (const v of [a, b, c, a, c, d]) { pos.push(v[0], v[1], v[2]); nrm.push(n[0], n[1], n[2]) }
  }
  return { positions: { value: new Float32Array(pos), size: 3 }, normals: { value: new Float32Array(nrm), size: 3 } }
}

// ---------------------------------------------------------------------------
// The bus's local frame. X = length (travel), Y = width, Z = up, origin on the road
// (z = 0), so the bus sits on the pavement with no altitude lift and the yaw orientation
// rotates it about its own vertical centreline.
//
// ⚠️ These are no longer a hand-drawn box. The bus IS the authored New Flyer Xcelsior 40
// (`xcel40Model.ts` → `mta-assets` `BUS-XCEL-40`), and every figure below is that mesh's
// own measured extent, so anything laid out against the bus — the route badge, the Minion
// overlays — is placed against the geometry that actually draws. The five procedural
// meshes that used to live here (body, roof pod, glass, wheels, headlights) are gone; the
// glTF carries all of it, in twelve authored materials instead of five flat colours.
//
// What is left here is placement, not shape. The window-band and skirt figures went with
// the Minion overlays they positioned: a livery is measured against the panel's own
// extents (`XCEL40_PANEL` in xcel40Model.ts, read straight off the GLB), so nothing needs
// a second-hand copy of the Xcelsior's elevation any more.
// ---------------------------------------------------------------------------
export const BHX = XCEL40_FRONT_X       // half length — see XCEL40_FRONT_X on the offset
export const BHY = XCEL40_HALF_WIDTH_M  // half width
export const BUS_TOP = XCEL40_ROOF_Z    // roofline
// One scale for the bus and everything that rides on it, so the mesh and the badge over it
// cannot drift apart — see XCEL40_SIZE_SCALE for why a bus is drawn larger than life at
// all, and why that exaggeration is 1.5× and no longer the old box's 4.61×.
export const BUS_SIZE_SCALE = XCEL40_SIZE_SCALE
// Altitude (map-metres) the floating bus route badge hovers at, just above the roofline,
// the bus analog of R160_LABEL_ALT for trains. The clearance is in BODY metres and scaled
// with it, so the badge keeps the same gap over the roof at any size scale.
export const BUS_LABEL_ALT = (BUS_TOP + 0.82) * BUS_SIZE_SCALE

// The body, roof pod, glass, wheels and headlights were five hand-built quad meshes
// here, and the Minion face was six more bolted to the nose. All of it is the
// Xcelsior's glTF now: the body is real geometry in its own materials (xcel40Model.ts),
// and the face is PAINT -- `drawFront` on the Minion livery, landing on the front cap
// that `assign_end_cap` carves out of `MAT_BLOCKOUT_BODY`. The quad builders that served
// them are gone with them, and so is the last reason this file knew what a bus LOOKS
// like rather than where one is. What remains below is placement: badges, glide math
// and shape matching.

// `dir` = which way this slice runs along the SOURCE shape's vertex order (+1 with it,
// -1 against it). Only the route-line offset needs it: MapLibre's `line-offset` pushes a
// line to the right of its own vertex order, so a train can only be shifted onto its drawn
// line once we know whether it is travelling with or against that order. See
// ROUTE_LINE_OFFSET_SCALE.
// `back` = the parent shape's vertices immediately BEHIND this slice's start, in travel
// order (its last element is the vertex adjacent to pts[0]), covering ~a consist length.
// A slice only spans prevStop→nextStop, so a train near the start of one has almost no
// track behind it to lay its cars on; `back` continues through the previous station so
// the tail can follow the real line instead of extending straight off a curve. Seed-time
// only; see seedTrail.
export interface ShapeSeg {
  pts: [number, number][]
  lens: number[]
  dir?: 1 | -1
  back?: [number, number][]
  /** Route feature whose canonical geometry produced this slice. */
  sourceRouteId?: string
}

// How much track to retain behind a slice: one consist plus margin, so seedTrail never
// runs out of real geometry and falls back to the straight extension.
const BACK_TRACK_M = TRAIL_KEEP_M + 40

export function closestShapeIdx(shape: [number, number][], lon: number, lat: number): number {
  const cosLat = Math.cos(lat * Math.PI / 180)
  let best = 0, bestD = Infinity
  for (let i = 0; i < shape.length; i++) {
    const dx = (shape[i][0] - lon) * cosLat, dy = shape[i][1] - lat
    const d = dx * dx + dy * dy
    if (d < bestD) { bestD = d; best = i }
  }
  return best
}

export function buildShapeSeg(shape: [number, number][], ai: number, bi: number): ShapeSeg {
  const fwd = ai <= bi
  const pts: [number, number][] = fwd
    ? (shape.slice(ai, bi + 1) as [number, number][])
    : (shape.slice(bi, ai + 1).reverse() as [number, number][])
  const lens = [0]
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0], dy = pts[i][1] - pts[i - 1][1]
    lens.push(lens[i - 1] + Math.sqrt(dx * dx + dy * dy))
  }
  // Keep BACK_TRACK_M of the shape behind the slice start. Step away from `ai` against
  // the travel direction, then reverse so the result reads oldest → newest like the
  // slice itself. Bounded by metres, not vertex count, since shape density varies.
  const back: [number, number][] = []
  const step = fwd ? -1 : 1
  let acc = 0
  let [clon, clat] = shape[ai]
  for (let i = ai + step; i >= 0 && i < shape.length && acc < BACK_TRACK_M; i += step) {
    acc += approxMeters(clon, clat, shape[i][0], shape[i][1])
    ;[clon, clat] = shape[i]
    back.push(shape[i])
  }
  back.reverse()
  return { pts, lens, dir: fwd ? 1 : -1, back }
}

interface ShapeProjection {
  segmentIndex: number
  fraction: number
  point: [number, number]
  distanceSq: number
}

function projectOntoShape(
  shape: [number, number][],
  coordinate: { lat: number; lon: number },
): ShapeProjection | null {
  if (shape.length < 2) return null
  const cosLat = Math.cos(coordinate.lat * Math.PI / 180)
  let best: ShapeProjection | null = null
  for (let index = 0; index < shape.length - 1; index++) {
    const start = shape[index]
    const end = shape[index + 1]
    const dx = (end[0] - start[0]) * cosLat
    const dy = end[1] - start[1]
    const lengthSq = dx * dx + dy * dy
    const fromStartX = (coordinate.lon - start[0]) * cosLat
    const fromStartY = coordinate.lat - start[1]
    const fraction = lengthSq > 0
      ? Math.max(0, Math.min(1, (fromStartX * dx + fromStartY * dy) / lengthSq))
      : 0
    const point: [number, number] = [
      start[0] + (end[0] - start[0]) * fraction,
      start[1] + (end[1] - start[1]) * fraction,
    ]
    const ex = (point[0] - coordinate.lon) * cosLat
    const ey = point[1] - coordinate.lat
    const distanceSq = ex * ex + ey * ey
    if (!best || distanceSq < best.distanceSq) {
      best = { segmentIndex: index, fraction, point, distanceSq }
    }
  }
  return best
}

function appendDistinctPoint(points: [number, number][], point: [number, number]): void {
  const previous = points[points.length - 1]
  if (!previous || previous[0] !== point[0] || previous[1] !== point[1]) points.push(point)
}

function projectedShapeSeg(
  shape: [number, number][],
  start: ShapeProjection,
  end: ShapeProjection,
  sourceRouteId?: string,
): ShapeSeg | null {
  const startPosition = start.segmentIndex + start.fraction
  const endPosition = end.segmentIndex + end.fraction
  if (Math.abs(startPosition - endPosition) < 1e-9) return null
  const fwd = startPosition < endPosition
  const pts: [number, number][] = []
  appendDistinctPoint(pts, start.point)
  if (fwd) {
    for (let index = Math.floor(startPosition) + 1; index < endPosition; index++) {
      appendDistinctPoint(pts, shape[index])
    }
  } else {
    for (let index = Math.ceil(startPosition) - 1; index > endPosition; index--) {
      appendDistinctPoint(pts, shape[index])
    }
  }
  appendDistinctPoint(pts, end.point)
  if (pts.length < 2) return null

  // Arc-length sampling in local ground units keeps speed even while every sampled
  // position remains an interpolation of the exact canonical line coordinates.
  const lens = [0]
  for (let index = 1; index < pts.length; index++) {
    const meanLatitude = (pts[index - 1][1] + pts[index][1]) / 2
    const cosLat = Math.cos(meanLatitude * Math.PI / 180)
    const dx = (pts[index][0] - pts[index - 1][0]) * cosLat
    const dy = pts[index][1] - pts[index - 1][1]
    lens.push(lens[index - 1] + Math.hypot(dx, dy))
  }

  // Retain canonical geometry behind the projected start for initial consist layout.
  const back: [number, number][] = []
  const step = fwd ? -1 : 1
  let index = fwd ? Math.floor(startPosition) : Math.ceil(startPosition)
  let cursor = start.point
  let accumulatedMetres = 0
  while (index >= 0 && index < shape.length && accumulatedMetres < BACK_TRACK_M) {
    const candidate = shape[index]
    if (candidate[0] !== cursor[0] || candidate[1] !== cursor[1]) {
      accumulatedMetres += approxMeters(cursor[0], cursor[1], candidate[0], candidate[1])
      back.push(candidate)
      cursor = candidate
    }
    index += step
  }
  back.reverse()
  return { pts, lens, dir: fwd ? 1 : -1, back, sourceRouteId }
}

// Bus-only adaptation of a canonical slice: begin at the bus's current rendered GPS
// position and end at its next live fix. Subway motion must never use this function because
// its endpoints would leave the route-line source of truth. Buses do not draw their entire
// base route network, so continuity with live GPS is the governing invariant here.
export function anchorGlideTrack(
  seg: ShapeSeg,
  fromLon: number, fromLat: number,
  toLon: number, toLat: number,
): ShapeSeg | null {
  const cosLat = Math.cos(fromLat * Math.PI / 180)
  const dx = (toLon - fromLon) * cosLat, dy = toLat - fromLat
  const chordSq = dx * dx + dy * dy
  const interior: [number, number][] = []
  if (chordSq > 0) {
    for (const [lon, lat] of seg.pts) {
      // Forward projection of this vertex onto the from→to chord, normalized to (0,1).
      const f = (((lon - fromLon) * cosLat) * dx + (lat - fromLat) * dy) / chordSq
      if (f > 0 && f < 1) interior.push([lon, lat])
    }
  }
  const pts: [number, number][] = [[fromLon, fromLat], ...interior, [toLon, toLat]]
  if (pts.length < 2) return null
  // cosLat-correct the cumulative lengths so arc-length sampling advances at an even
  // ground speed (NYC longitude degrees are ~0.76x a latitude degree).
  const lens = [0]
  for (let i = 1; i < pts.length; i++) {
    const ex = (pts[i][0] - pts[i - 1][0]) * cosLat, ey = pts[i][1] - pts[i - 1][1]
    lens.push(lens[i - 1] + Math.sqrt(ex * ex + ey * ey))
  }
  // `back` survives re-anchoring: it describes the parent shape behind this slice, which
  // pinning the endpoints doesn't change. seedTrail trims the join itself, since pts[0] is
  // now the true stop rather than the vertex `back` was measured against.
  return { pts, lens, dir: seg.dir, back: seg.back, sourceRouteId: seg.sourceRouteId }
}

export function sampleSeg(seg: ShapeSeg, t: number): [number, number] {
  const { pts, lens } = seg
  if (pts.length === 1) return pts[0]
  const total = lens[lens.length - 1]
  const target = total * Math.max(0, Math.min(1, t))
  for (let i = 1; i < pts.length; i++) {
    if (lens[i] >= target || i === pts.length - 1) {
      const span = lens[i] - lens[i - 1]
      const frac = span > 0 ? (target - lens[i - 1]) / span : 0
      return [
        pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * frac,
        pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * frac,
      ]
    }
  }
  return pts[pts.length - 1]
}

// Quick planar metres between two lon/lat points (equirectangular; accurate at city scale).
// Scalar-argument façades over the consist geometry's point versions. The formulas live
// in trainConsist.ts and exist EXACTLY once: the trail code and the glide code have to
// agree to the metre or a train re-seeds its trail on the RENDER_SNAP_M boundary. Only
// the call shape differs: this half of the file works in loose lon/lat scalars.
export function approxMeters(lon1: number, lat1: number, lon2: number, lat2: number): number {
  return metresBetween({ lon: lon1, lat: lat1 }, { lon: lon2, lat: lat2 })
}

export function calcBearing(fromLon: number, fromLat: number, toLon: number, toLat: number): number {
  return bearingBetween({ lon: fromLon, lat: fromLat }, { lon: toLon, lat: toLat })
}

// Best-fitting segment of `shapes` between two stops, plus the combined
// (cosLat-corrected) squared-degree distance from each stop to its nearest projection
// on the chosen shape. That distance reveals whether the shapes actually cover the
// stop pair: a route that doesn't serve this corridor scores orders of magnitude worse.
export function matchSegmentInShapes(
  shapes: [number, number][][] | undefined,
  prevC: { lat: number; lon: number },
  nextC: { lat: number; lon: number },
  sourceRouteId?: string,
): { seg: ShapeSeg | null; dist: number } {
  let seg: ShapeSeg | null = null
  let best = Infinity
  if (!shapes) return { seg, dist: best }
  for (const shape of shapes) {
    if (shape.length < 2) continue
    const start = projectOntoShape(shape, prevC)
    const end = projectOntoShape(shape, nextC)
    if (!start || !end) continue
    const distance = start.distanceSq + end.distanceSq
    const candidate = projectedShapeSeg(shape, start, end, sourceRouteId)
    if (candidate && distance < best) {
      best = distance
      seg = candidate
    }
  }
  return { seg, dist: best }
}

// Local track tangent at parameter t along a ShapeSeg.
export function bearingAtT(seg: ShapeSeg, t: number): number {
  const e = 0.0005
  const [lo1, la1] = sampleSeg(seg, Math.max(0, t - e))
  const [lo2, la2] = sampleSeg(seg, Math.min(1, t + e))
  if (Math.abs(lo1 - lo2) < 1e-10 && Math.abs(la1 - la2) < 1e-10) return 0
  return calcBearing(lo1, la1, lo2, la2)
}

// ---------------------------------------------------------------------------
// Breadcrumb trail: the multi-car BENDING mechanism. Each subway's rendered
// center position is recorded (one point per TRAIL_STEP_M of movement) into a
// per-train polyline with cumulative arc length. Follower cars sample this
// polyline at fixed arc offsets behind the front car, so the train bends along
// whatever path the front actually traced: curves, hand-offs, everything. A
// follower can only ever occupy a point the (already smoothed) center has
// occupied, so followers are exactly as smooth as the front car by
// construction. This is what makes bending safe, unlike the original per-car
// track sampling, where each car independently snapped to shape geometry and
// popped at every segment hand-off.
// ---------------------------------------------------------------------------
export type TrailPt = TrailPoint

// Fresh trail for a train first seen (or re-placed > RENDER_SNAP_M). Walks
// BACKWARD along the train's current track slice so the tail lies on the real
// curve immediately instead of straight across blocks; when the slice behind is
// shorter than the train (it just departed the previous stop) or there is no
// slice, the remainder extends straight. Seed-time only; from then on the trail
// grows from real rendered positions.
// Vertices behind the slice start, in travel order, trimmed to `needM` metres of arc.
// The join is defensive for both projected subway slices and GPS-anchored bus slices:
// discard any retained vertex that falls ahead of pts[0] along the local tangent, so a
// malformed join can never make follower cars briefly run forward before running back.
function backPath(seg: ShapeSeg, needM: number): [number, number][] {
  const back = seg.back
  if (!back?.length || needM <= 0) return []
  const [p0lon, p0lat] = seg.pts[0]
  const [p1lon, p1lat] = seg.pts[1]
  const cosLat = Math.cos(p0lat * Math.PI / 180)
  const tx = (p1lon - p0lon) * cosLat, ty = p1lat - p0lat
  let end = back.length
  while (end > 0) {
    const [blon, blat] = back[end - 1]
    if (((blon - p0lon) * cosLat) * tx + (blat - p0lat) * ty <= 0) break
    end--
  }
  let start = end, acc = 0
  let clon = p0lon, clat = p0lat
  while (start > 0 && acc < needM) {
    start--
    acc += approxMeters(clon, clat, back[start][0], back[start][1])
    clon = back[start][0]
    clat = back[start][1]
  }
  return back.slice(start, end)
}

export function seedTrail(
  lon: number,
  lat: number,
  bearing: number,
  seg: ShapeSeg | null,
  t: number,
  sampleStepM = TRAIL_STEP_M,
): TrailPt[] {
  const raw: [number, number][] = []
  if (seg && seg.pts.length >= 2) {
    const arcM = seg.lens[seg.lens.length - 1] * 111_320
    const tBack = arcM > 0 ? Math.max(0, t - TRAIL_KEEP_M / arcM) : 0
    const coveredM = Math.max(0, (t - tBack) * arcM)
    // The slice alone only reaches back to the previous stop, so a train that just
    // departed one has almost nothing behind it. Continue along the parent shape,
    // through that station and onward, for whatever the consist still needs, instead
    // of extending the tail straight and throwing the rear cars off a curve.
    if (coveredM < TRAIL_KEEP_M) raw.push(...backPath(seg, TRAIL_KEEP_M - coveredM))
    if (coveredM > 0) {
      const steps = Math.max(24, Math.min(2048, Math.ceil(coveredM / Math.max(0.05, sampleStepM))))
      for (let i = 0; i <= steps; i++) raw.push(sampleSeg(seg, tBack + (t - tBack) * (i / steps)))
    }
  }
  return seedConsistTrail(
    raw.map(([pointLon, pointLat]) => ({ lon: pointLon, lat: pointLat })),
    { lon, lat },
    bearing,
    TRAIL_KEEP_M,
  )
}

